import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import useMenuData from './hooks/useMenuData';
import useSeo from '../../hooks/useSeo';
import { SquareLiveProvider } from './hooks/useSquareLive';
import SectionCard from './components/SectionCard';
import CombosSection from './components/CombosSection';
import ItemModal from './components/ItemModal';
import StoreStatus from './components/StoreStatus';
import OrderBar from './components/OrderBar';
import { LoadingSkeleton, ErrorState } from './components/StatusStates';
import { OrderingProvider } from './ordering/OrderingContext';
import CartFab from './ordering/CartFab';
import CartDrawer from './ordering/CartDrawer';
import CheckoutModal from './ordering/CheckoutModal';
import MappingEditor from './ordering/MappingEditor';
import { AddCategoryPanel, CategoryOrderPanel } from '../../Components/AdminEdit/CategoryTools';
import {
  CATEGORY_LAYOUTS, COMBOS_ID, orderedCategories, moveCategory,
  addSection, renameSection, deleteSection,
} from './utils/menuHelpers';
import './ordering/ordering.css';
import './FoodMenu.css';
import '../Landing/Landing.css';
import '../../Components/AdminEdit/AdminEditBar.css';

/* ── Hot-item images: optimized ~400px AVIF + JPEG in /public/images/hotitems.
   Served as static files (not webpack-bundled) so the giant originals in
   src/Images/hotitems are no longer shipped. ── */
const HOT_IMG_BASE = '/images/hotitems';

/**
 * Category icon map for the navigation bar.
 */
const CAT_ICONS = {
  'Dosa / Pesarattu / Ragi Dosa': '🫓',
  'Idly': '🍚',
  'Uttappam': '🥞',
  'Combos': '🎁',
  'Street Bites': '🥙',
  'Appetizers': '🍢',
  'Indo Chinese': '🥢',
  'Biryanis & Pulaos': '🍛',
  'Curries': '🫕',
  'Breads': '🫓',
  'Chaats': '🌮',
  'Beverages & Desserts': '🥤',
};

const getCategoryLabel = (cat) => (cat === 'Combos' ? 'Combos and Specials' : cat);

/* ── Hot items / bestsellers for scrolling carousel ── */
const HOT_ITEMS = [
  { name: 'Mysore Bonda', tag: '🔥 Hot', img: 'mysore-bonda' },
  { name: 'Punugulu', tag: '⭐ Popular', img: 'punugulu' },
  { name: 'Stuffed Mirchi Bajji', tag: '🌶️ Spicy', img: 'mirchi-bajji' },
  { name: 'Ghee Podi Idly', tag: '🔥 Hot', img: 'ghee-podi-idly' },
  { name: 'Ghee Karampodi Onion Masala Dosa', tag: '⭐ Bestseller', img: 'masala-dosa' },
  { name: 'Poori', tag: '❤️ Classic', img: 'poori' },
  { name: 'Parotta with Chicken', tag: '🔥 Hot', img: 'parotta-chicken' },
  { name: 'Samosa Chaat', tag: '⭐ Fan Fav', img: 'samosa-Chaat' },
  { name: 'Paneer 555', tag: '🌶️ Spicy', img: 'paneer-555' },
  { name: 'Chilli Chicken', tag: '🔥 Hot', img: 'chilli-chicken' },
  { name: 'Veg Manchuria', tag: '⭐ Popular', img: 'veg-manchuria' },
  { name: 'Veg Keema Biryani', tag: '🌿 Veg', img: 'veg-keema-biryani' },
  { name: 'Raju Gari Kodi Pulao', tag: '👑 Signature', img: 'kodi-pulao' },
  { name: 'Andhra Goat Fry Biryani', tag: '🔥 #1 Seller', img: 'goat-biryani' },
];

/**
 * FoodMenu — Main menu page component.
 * Features sticky category nav, collapsible sections, compact combos.
 */
const FoodMenu = () => {
  const { menuData, setMenuData, loading, error, refetch, stale } = useMenuData();
  const { role } = useAuth();
  const canEdit = role === 'admin' || role === 'owner' || role === 'manager';
  const canEditPrice = role === 'admin' || role === 'owner';

  useSeo({
    title: 'Menu',
    description: 'Explore the full Desi Bites menu — dosas, idly, uttapam, biryanis, curries, Indo-Chinese, chaats, combos & desserts. Order pickup or delivery in Centerton, AR.',
    path: '/menu',
  });

  // ── Edit mode state ──
  const [isEditing, setIsEditing] = useState(false);
  const [dirty, setDirty]       = useState(false);
  const [saving, setSaving]     = useState(false);
  const [originalData, setOriginalData] = useState(null); // snapshot for discard
  const [searchQuery, setSearchQuery] = useState('');
  const searchRef = useRef(null);

  const enterEditMode = useCallback(() => {
    // The on-screen menu may be this device's cached copy for a moment; editing
    // that could publish over a newer menu, so wait for the live one.
    if (stale) return;
    setOriginalData(JSON.parse(JSON.stringify(menuData))); // deep clone snapshot
    setIsEditing(true);
    setDirty(false);
    // The search box is hidden while editing, so a leftover query would keep
    // every section — and the add-category panel — behind the results list.
    setSearchQuery('');
  }, [menuData, stale]);

  const discardEdits = useCallback(() => {
    if (originalData) setMenuData(originalData);
    setIsEditing(false);
    setDirty(false);
  }, [originalData, setMenuData]);

  const publish = useCallback(async () => {
    if (!menuData) return;
    setSaving(true);
    try {
      await setDoc(doc(db, 'menuConfig', 'main'), { _json: JSON.stringify(menuData) });
      setDirty(false);
      setIsEditing(false);
    } catch (e) {
      alert('Failed to publish: ' + e.message);
    } finally {
      setSaving(false);
    }
  }, [menuData]);

  // Helper: mark dirty and update menuData
  const updateMenu = useCallback((updater) => {
    setMenuData(typeof updater === 'function' ? updater : () => updater);
    setDirty(true);
  }, [setMenuData]);

  // Section update handlers
  const handleUpdateSection = useCallback((sectionIdx, updatedSection) => {
    updateMenu((prev) => ({
      ...prev,
      sections: prev.sections.map((s, i) => (i === sectionIdx ? updatedSection : s)),
    }));
  }, [updateMenu]);

  const handleUpdateCombos = useCallback((updatedCombos) => {
    updateMenu((prev) => ({ ...prev, combos: updatedCombos }));
  }, [updateMenu]);

  /* ── Category create / rename / delete / reorder ──
     The reducers are pure functions in menuHelpers; what stays here is the
     view state they have to keep in step — which section is expanded, and
     where to scroll. ── */
  const handleAddSection = useCallback((spec) => {
    let section;
    updateMenu((prev) => {
      const next = addSection(prev, spec);
      section = next.sections.at(-1);
      return next;
    });
    // Open it straight away — an empty collapsed category looks like nothing
    // happened — and scroll once React has rendered the new card.
    setExpandedSections((prev) => new Set(prev).add(section.title));
    requestAnimationFrame(() => {
      setTimeout(() => {
        sectionRefs.current[section.title]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 80);
    });
  }, [updateMenu]);

  const handleRenameSection = useCallback((sectionIdx, oldTitle, name) => {
    updateMenu((prev) => renameSection(prev, sectionIdx, name));
    // Expansion and the nav are keyed by title, so carry the open state over.
    setExpandedSections((open) => {
      if (!open.has(oldTitle)) return open;
      const next = new Set(open);
      next.delete(oldTitle);
      next.add(name);
      return next;
    });
  }, [updateMenu]);

  const handleDeleteSection = useCallback((sectionIdx) => {
    updateMenu((prev) => deleteSection(prev, sectionIdx));
  }, [updateMenu]);

  /* `from`/`to` are positions in the combined category list (combos included),
     not indexes into `sections` — see orderedCategories. */
  const handleMoveCategory = useCallback((from, to) => {
    updateMenu((prev) => moveCategory(prev, from, to));
  }, [updateMenu]);

  const [selectedItem, setSelectedItem] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);
  const [expandedSections, setExpandedSections] = useState(new Set());
  const sectionRefs = useRef({});
  const navRef = useRef(null);

  /* ── Hot-items draggable + auto-scroll ── */
  const hotTrackRef = useRef(null);
  const hotDrag = useRef({ down: false, startX: 0, scrollLeft: 0 });

  // Auto-scroll: slow continuous scroll when user is not interacting
  useEffect(() => {
    const el = hotTrackRef.current;
    if (!el) return;
    let raf;
    let paused = false;
    const speed = 0.5; // px per frame

    const step = () => {
      if (!paused && el.scrollLeft < el.scrollWidth - el.clientWidth) {
        el.scrollLeft += speed;
      } else if (!paused) {
        el.scrollLeft = 0; // loop back
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);

    const pause = () => { paused = true; };
    const resume = () => { setTimeout(() => { paused = false; }, 2000); };

    el.addEventListener('pointerdown', pause);
    el.addEventListener('pointerup', resume);
    el.addEventListener('pointerleave', resume);
    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener('pointerdown', pause);
      el.removeEventListener('pointerup', resume);
      el.removeEventListener('pointerleave', resume);
    };
  }, []);

  const onHotPointerDown = useCallback((e) => {
    const el = hotTrackRef.current;
    if (!el) return;
    hotDrag.current = { down: true, startX: e.pageX - el.offsetLeft, scrollLeft: el.scrollLeft };
    el.setPointerCapture(e.pointerId);
  }, []);
  const onHotPointerMove = useCallback((e) => {
    if (!hotDrag.current.down) return;
    const el = hotTrackRef.current;
    const x = e.pageX - el.offsetLeft;
    el.scrollLeft = hotDrag.current.scrollLeft - (x - hotDrag.current.startX);
  }, []);
  const onHotPointerUp = useCallback(() => { hotDrag.current.down = false; }, []);

  const handleSelect = useCallback((item) => {
    setSelectedItem(item);
  }, []);

  const handleClose = useCallback(() => {
    setSelectedItem(null);
  }, []);

  /* The combos block and the sections render as one sequence, and everything
     keyed by category — the nav rail, the scroll spy, the order panel — reads
     that same sequence so they can never disagree about the order. */
  const showCombos = menuData?.combos?.length > 0 || isEditing;
  const catEntries = useMemo(
    () => (menuData ? orderedCategories(menuData, { withCombos: showCombos }) : []),
    [menuData, showCombos],
  );

  const categories = useMemo(
    () => catEntries.map((e) => (e.type === 'combos' ? 'Combos' : e.section.title)),
    [catEntries],
  );

  // Icons: a category created in the editor carries its own emoji, otherwise
  // fall back to the built-in map.
  const catIcons = useMemo(() => {
    const map = { ...CAT_ICONS };
    menuData?.sections?.forEach((s) => { if (s.icon) map[s.title] = s.icon; });
    return map;
  }, [menuData]);

  // Flat search index: [{name, desc, section, price, item}]
  const searchIndex = useMemo(() => {
    if (!menuData) return [];
    const results = [];
    // Section items
    menuData.sections?.forEach((section) => {
      section.columns?.forEach((col) => {
        col?.forEach((card) => {
          if (card?.name) {
            // Card itself is the menu item (e.g. Street Bites, Appetizers…)
            results.push({ name: card.name, desc: card.desc || '', price: card.price ?? null, section: section.title, item: { name: card.name, desc: card.desc || '', img: card.img, price: card.price ?? null } });
          } else if (card?.items?.length) {
            // Card is a group wrapper with sub-items
            card.items.forEach((it) => {
              const name = typeof it === 'string' ? it : it?.name || '';
              const desc = typeof it === 'object' ? (it?.desc || '') : '';
              const price = typeof it === 'object' ? (it?.price ?? null) : null;
              if (name) results.push({ name, desc, price, section: section.title, item: { name, desc, img: it?.img, price } });
            });
          }
        });
      });

      // A price matrix prints one row per style, so expand it back to real dish
      // names — "ghee karampodi onion masala" must still find all three bases.
      if (Array.isArray(section.rows) && Array.isArray(section.priceColumns)) {
        section.rows.forEach(([style, ...prices]) => {
          if (!style) return;
          section.priceColumns.forEach((base, i) => {
            const name = style === 'Plain' ? base : `${style} ${base}`;
            const desc = section.columnNotes?.[base] || '';
            const price = typeof prices[i] === 'number' ? prices[i] : null;
            results.push({ name, desc, price, section: section.title, item: { name, desc, price } });
          });
        });
      }
    });
    // Combo cards
    menuData.combos?.forEach((group) => {
      group.cards?.forEach((card) => {
        if (card?.name) results.push({ name: card.name, desc: card.desc || '', price: card.price ?? null, section: 'Combos & Specials', item: { name: card.name, desc: card.desc || '', price: card.price ?? null } });
      });
    });
    return results;
  }, [menuData]);

  const searchResults = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return [];
    return searchIndex.filter((r) => r.name.toLowerCase().includes(q) || r.desc.toLowerCase().includes(q));
  }, [searchQuery, searchIndex]);
  /* Expand each category the first time it appears, then leave the set alone.
     Rebuilding it from `categories` on every change meant any edit — a renamed
     item, a new combo group — snapped the sections back to their default state,
     re-collapsing Combos while the admin was working inside it. */
  const seenCategories = useRef(new Set());
  useEffect(() => {
    if (!categories.length) return;
    const fresh = categories.filter((c) => !seenCategories.current.has(c));
    if (!fresh.length) return;
    fresh.forEach((c) => seenCategories.current.add(c));
    const toOpen = fresh.filter((c) => c !== 'Combos');
    if (toOpen.length) setExpandedSections((prev) => new Set([...prev, ...toOpen]));
  }, [categories]);

  // Toggle section expand/collapse
  const toggleSection = useCallback((name) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  }, []);

  // Scroll to a section when category pill is tapped
  const scrollToCategory = useCallback((catName) => {
    setActiveCategory(catName);
    // Expand section if collapsed
    setExpandedSections((prev) => {
      const next = new Set(prev);
      next.add(catName);
      return next;
    });
    // Wait for React to re-render (section expand) before scrolling
    requestAnimationFrame(() => {
      setTimeout(() => {
        const el = sectionRefs.current[catName];
        if (el) {
          const navHeight = navRef.current?.offsetHeight || 52;
          const top = el.getBoundingClientRect().top + window.scrollY - navHeight - 8;
          window.scrollTo({ top, behavior: 'smooth' });
        }
      }, 60);
    });
  }, []);

  // Intersection observer — highlight active pill on scroll
  useEffect(() => {
    if (!categories.length) return;
    const navH = navRef.current?.offsetHeight || 52;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setActiveCategory(e.target.dataset.cat);
            // Scroll pill into view in nav
            const pill = navRef.current?.querySelector(`[data-nav="${e.target.dataset.cat}"]`);
            pill?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
          }
        }
      },
      { rootMargin: `-${navH + 16}px 0px -60% 0px`, threshold: 0.1 }
    );
    Object.values(sectionRefs.current).forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [categories, menuData]);

  /* Deep-link: auto-scroll to category from ?cat= query param */
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const cat = params.get('cat');
    if (cat && categories.includes(cat)) {
      setTimeout(() => scrollToCategory(cat), 400);
    }
  }, [location.search, categories, scrollToCategory]);

  return (
    <OrderingProvider role={role}>
    <SquareLiveProvider>
    <main className={`fm-page${isEditing ? ' ae-editing-body' : ''}`}>
      {/* Decorative ambient blobs */}
      <div className="fm-page__ambient fm-page__ambient--top" aria-hidden="true" />
      <div className="fm-page__ambient fm-page__ambient--bottom" aria-hidden="true" />

      {/* ── Admin: floating edit toggle ── */}
      {canEdit && !isEditing && (
        <button className="ae-toggle" onClick={enterEditMode} disabled={!!stale}
          title={stale ? 'Loading the latest menu…' : 'Enter edit mode'}>
          ✏️ Edit Menu
        </button>
      )}

      {/* ── Admin: publish / discard bar ── */}
      {isEditing && (
        <div className="ae-bar">
          <span className="ae-bar__info">
            <strong>✏️ Edit Mode</strong> — {dirty ? 'Unsaved changes · ' : 'No changes yet · '}
            Click ✏️ on any item to edit it
          </span>
          <div className="ae-bar__actions">
            <button className="ae-btn-discard" onClick={discardEdits}>✕ Discard</button>
            <button className="ae-btn-publish" onClick={publish} disabled={saving || !dirty}>
              {saving ? 'Publishing…' : '✓ Publish Changes'}
            </button>
          </div>
        </div>
      )}

      {/* Hot Items Carousel — draggable + auto-scroll */}
      <section className="lp-hot fm-hot-section">
        <div className="lp-hot__inner" style={{ marginBottom: 10 }}>
          <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 'clamp(22px, 4vw, 32px)', fontWeight: 800, color: '#fff', margin: '6px 0 0' }}>🔥 Most Ordered Items</h2>
        </div>
        <div
          className="lp-hot__track"
          ref={hotTrackRef}
          onPointerDown={onHotPointerDown}
          onPointerMove={onHotPointerMove}
          onPointerUp={onHotPointerUp}
        >
          <div className="lp-hot__scroll">
            {[...HOT_ITEMS, ...HOT_ITEMS].map((item, i) => (
              <div key={i} className="lp-hot__card">
                <div className="lp-hot__img-wrap">
                  <picture>
                    <source srcSet={`${HOT_IMG_BASE}/${item.img}.avif`} type="image/avif" />
                    <img src={`${HOT_IMG_BASE}/${item.img}.jpg`} alt={item.name} className="lp-hot__img" loading="lazy" decoding="async" draggable={false} />
                  </picture>
                  <div className="lp-hot__img-overlay" />
                </div>
                <div className="lp-hot__name">{item.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sticky category nav */}
      {menuData && categories.length > 0 && (
        <nav className="fm-catnav" ref={navRef}>
          <span className="fm-catnav__label">Menu</span>
          <div className="fm-catnav__track">
            {categories.map((cat) => (
              <button
                key={cat}
                data-nav={cat}
                className={`fm-catnav__tab${activeCategory === cat ? ' fm-catnav__tab--active' : ''}`}
                onClick={() => scrollToCategory(cat)}
              >
                <span className="fm-catnav__tab-icon">{catIcons[cat] || '🍽️'}</span>
                <span className="fm-catnav__tab-label">{getCategoryLabel(cat)}</span>
              </button>
            ))}
          </div>
        </nav>
      )}

      {/* Search bar */}
      {menuData && !isEditing && (
        <div className="fm-search">
          <div className="fm-search__inner">
            <svg className="fm-search__icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="7"/><line x1="16.5" y1="16.5" x2="21" y2="21"/>
            </svg>
            <input
              ref={searchRef}
              type="search"
              className="fm-search__input"
              placeholder="Search menu items…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Search menu items"
            />
            {searchQuery && (
              <button className="fm-search__clear" onClick={() => { setSearchQuery(''); searchRef.current?.focus(); }} aria-label="Clear search">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            )}
          </div>
        </div>
      )}

      <div className="fm-shell">
        <div className="fm-viewport">
          {loading ? (
            <LoadingSkeleton />
          ) : error ? (
            <ErrorState message={error} onRetry={refetch} />
          ) : menuData ? (
            <div className="fm-content">
              {/* Page title */}
              <div className="fm-page-hero">
                <div className="fm-hero-row">
                  <h1 className="fm-page-hero__title">Bold Indian Flavors.</h1>
                  <div className="fm-hero-row__actions">
                    <StoreStatus />
                    <Link to="/menu-poster" className="fm-poster-btn" title="Design & print a menu poster">
                      🖨️ Print Menu Poster
                    </Link>
                  </div>
                </div>
              </div>

              {/* ── Search results ── */}
              {searchQuery.trim() ? (
                <div className="fm-search-results">
                  {searchResults.length === 0 ? (
                    <div className="fm-search-empty">
                      <span className="fm-search-empty__icon">🔍</span>
                      <p>No items found for <strong>"{searchQuery}"</strong></p>
                    </div>
                  ) : (
                    <>
                      <p className="fm-search-count">{searchResults.length} result{searchResults.length !== 1 ? 's' : ''} for <strong>"{searchQuery}"</strong></p>
                      {searchResults.map((r, i) => (
                        <button
                          key={i}
                          className="fm-search-row"
                          onClick={() => handleSelect(r.item)}
                        >
                          <div className="fm-search-row__body">
                            <span className="fm-search-row__name">{r.name}</span>
                            {r.desc && <span className="fm-search-row__desc">{r.desc}</span>}
                            <span className="fm-search-row__section">{r.section}</span>
                          </div>
                          {r.price != null && <span className="fm-search-row__price">${r.price.toFixed(2)}</span>}
                        </button>
                      ))}
                    </>
                  )}
                </div>
              ) : (
                <>
                  {/* ── Admin: reorder every category from one list ── */}
                  {isEditing && (
                    <CategoryOrderPanel
                      title="Category order"
                      items={catEntries.map((e) => (e.type === 'combos'
                        ? {
                          id: COMBOS_ID,
                          icon: '🎁',
                          label: 'Combos and Specials',
                          hint: `${(menuData.combos || []).reduce((n, g) => n + (g.cards?.length || 0), 0)} combos`,
                        }
                        : {
                          id: e.section.title,
                          icon: catIcons[e.section.title] || '🍽️',
                          label: e.section.title,
                          hint: e.section.rows
                            ? `${e.section.rows.length} × ${e.section.priceColumns.length}`
                            : `${(e.section.columns || []).flat().length} items`,
                        }))}
                      onMove={handleMoveCategory}
                      onJump={(id) => scrollToCategory(id === COMBOS_ID ? 'Combos' : id)}
                    />
                  )}

                  {/* ── The categories themselves, in that same order ── */}
                  {catEntries.map((entry, pos) => {
                    const isCombos = entry.type === 'combos';
                    const title = isCombos ? 'Combos' : entry.section.title;
                    const label = isCombos ? 'Combos and Specials' : title;
                    const count = isCombos
                      ? `${(menuData.combos || []).reduce((n, g) => n + (g.cards?.length || 0), 0)} combos`
                      : entry.section.rows
                        ? `${entry.section.rows.length} × ${entry.section.priceColumns.length} prices`
                        : `${(entry.section.columns || []).flat().length} items`;

                    return (
                      <div
                        key={isCombos ? COMBOS_ID : `${title}-${entry.index}`}
                        className="fm-section"
                        ref={(el) => (sectionRefs.current[title] = el)}
                        data-cat={title}
                      >
                        <button
                          className={`fm-section__toggle${expandedSections.has(title) ? ' fm-section__toggle--open' : ''}`}
                          onClick={() => toggleSection(title)}
                        >
                          <span className="fm-section__toggle-icon">
                            {isCombos ? '🎁' : (entry.section.icon || CAT_ICONS[title] || '🍽️')}
                          </span>
                          <span className="fm-section__toggle-label">{label}</span>
                          <span className="fm-section__toggle-count">{count}</span>
                          <span className="fm-section__toggle-arrow">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                              <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </span>
                        </button>
                        {expandedSections.has(title) && (
                          <div className="fm-section__body fm-section__body--animate">
                            {isCombos ? (
                              <CombosSection
                                combos={menuData.combos}
                                isEditing={isEditing}
                                canEditPrice={canEditPrice}
                                onUpdate={handleUpdateCombos}
                                onMoveUp={pos > 0 ? () => handleMoveCategory(pos, pos - 1) : undefined}
                                onMoveDown={pos < catEntries.length - 1 ? () => handleMoveCategory(pos, pos + 1) : undefined}
                              />
                            ) : (
                              <SectionCard
                                section={entry.section}
                                onSelect={handleSelect}
                                isEditing={isEditing}
                                canEditPrice={canEditPrice}
                                onUpdate={(updated) => handleUpdateSection(entry.index, updated)}
                                takenNames={menuData.sections.map((x) => x.title)}
                                onRename={(name) => handleRenameSection(entry.index, title, name)}
                                onDelete={() => handleDeleteSection(entry.index)}
                                onMoveUp={pos > 0 ? () => handleMoveCategory(pos, pos - 1) : undefined}
                                onMoveDown={pos < catEntries.length - 1 ? () => handleMoveCategory(pos, pos + 1) : undefined}
                              />
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* ── Admin: create a new category ── */}
                  {isEditing && (
                    <AddCategoryPanel
                      label="+ Add Category"
                      heading="New menu category"
                      namePlaceholder="Category name — e.g. Soups"
                      options={CATEGORY_LAYOUTS}
                      takenNames={menuData.sections.map((s) => s.title)}
                      onCreate={handleAddSection}
                    />
                  )}
                </>
              )}
            </div>
          ) : (
            <ErrorState message="Menu not found" />
          )}
        </div>

        {/* Item detail modal */}
        {selectedItem && (
          <ItemModal item={selectedItem} onClose={handleClose} />
        )}

      </div>

      {/* Spacer so the last items aren't hidden behind the sticky order bar */}
      <div style={{ height: 84 }} aria-hidden="true" />
    </main>

    {/* Sticky customer ordering bar → DoorDash (pickup & delivery) */}
    <OrderBar />

      {/* Online ordering (staff-only, sandbox test) */}
      <CartFab />
      <CartDrawer />
      <CheckoutModal />
      <MappingEditor />
    </SquareLiveProvider>
    </OrderingProvider>
  );
};

export default FoodMenu;
